// Bilateral stimulation sounds, synthesised in the browser with the Web Audio
// API so no audio files have to be downloaded or licensed. Each entry builds a
// short mono AudioBuffer; the caller pans it left/right on alternate beats.
// `onset` is how far into the sound (seconds) its peak is, which is lined up
// with the ball's turn. Keep the ids in sync with SOUND_IDS in backend/app.py.

const TWO_PI = Math.PI * 2;

function makeBuffer(ctx, duration, sample, peak) {
  const sampleRate = ctx.sampleRate;
  const length = Math.floor(sampleRate * duration);
  const buffer = ctx.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);
  const state = {};
  let max = 0;
  for (let i = 0; i < length; i++) {
    data[i] = sample(i / sampleRate, state);
    max = Math.max(max, Math.abs(data[i]));
  }
  // Normalise loudness and fade out the last 5 ms to avoid a click
  const gain = peak / (max || 1);
  const fade = Math.floor(sampleRate * 0.005);
  for (let i = 0; i < length; i++) {
    data[i] *= gain * Math.min(1, (length - i) / fade);
  }
  return buffer;
}

const noise = () => Math.random() * 2 - 1;

export const SOUNDS = [
  {
    id: 'beep',
    labelKey: 'controller.dock.soundBeep',
    build: ctx => makeBuffer(ctx, 0.1, t => Math.sin(TWO_PI * 880 * t) * Math.exp(-30 * t), 1)
  },
  {
    id: 'soft',
    labelKey: 'controller.dock.soundSoft',
    onset: 0.015,
    // Low 440 Hz hum with a gentle attack
    build: ctx => makeBuffer(ctx, 0.4, t =>
      (Math.sin(TWO_PI * 440 * t) + 0.25 * Math.sin(TWO_PI * 880 * t)) *
      Math.min(1, t / 0.015) * Math.exp(-7 * t), 0.8)
  },
  {
    id: 'tap',
    labelKey: 'controller.dock.soundTap',
    // Dry click, similar to handheld tappers
    build: ctx => makeBuffer(ctx, 0.04, t =>
      noise() * Math.exp(-150 * t) + 0.6 * Math.sin(TWO_PI * 1800 * t) * Math.exp(-120 * t), 0.8)
  },
  {
    id: 'wood',
    labelKey: 'controller.dock.soundWood',
    build: ctx => makeBuffer(ctx, 0.15, t =>
      Math.sin(TWO_PI * 950 * t) * Math.exp(-45 * t) +
      0.5 * Math.sin(TWO_PI * 1520 * t) * Math.exp(-60 * t) +
      0.2 * noise() * Math.exp(-400 * t), 0.8)
  },
  {
    id: 'chime',
    labelKey: 'controller.dock.soundChime',
    // Bell-like inharmonic partials with a long ring
    build: ctx => makeBuffer(ctx, 0.9, t =>
      Math.min(1, t / 0.003) * (
        Math.sin(TWO_PI * 880 * t) * Math.exp(-4 * t) +
        0.5 * Math.sin(TWO_PI * 2428 * t) * Math.exp(-6 * t) +
        0.2 * Math.sin(TWO_PI * 4752 * t) * Math.exp(-9 * t)
      ), 0.6)
  },
  {
    id: 'drop',
    labelKey: 'controller.dock.soundDrop',
    // Rising pitch sweep, like a water droplet
    build: ctx => makeBuffer(ctx, 0.18, (t, s) => {
      s.phase = (s.phase || 0) + TWO_PI * 600 * Math.exp(8 * t) / ctx.sampleRate;
      return Math.sin(s.phase) * Math.exp(-25 * t) * Math.min(1, t / 0.002);
    }, 0.8)
  },
  {
    id: 'drum',
    labelKey: 'controller.dock.soundDrum',
    // Soft low thump with a falling pitch, like a heartbeat
    build: ctx => makeBuffer(ctx, 0.35, (t, s) => {
      s.phase = (s.phase || 0) + TWO_PI * (90 + 80 * Math.exp(-25 * t)) / ctx.sampleRate;
      return (Math.sin(s.phase) + 0.3 * Math.sin(2 * s.phase)) * Math.exp(-12 * t);
    }, 0.95)
  },
  {
    id: 'swoosh',
    labelKey: 'controller.dock.soundSwoosh',
    onset: 0.225,
    // Low-passed white noise with a smooth swell, like a soft wave
    build: ctx => makeBuffer(ctx, 0.45, (t, s) => {
      s.y = (s.y || 0) + 0.08 * (noise() - (s.y || 0));
      return s.y * Math.pow(Math.sin(Math.PI * t / 0.45), 2);
    }, 0.7)
  }
];

export const DEFAULT_SOUND = 'beep';

function findSound(id) {
  return SOUNDS.find(s => s.id === id) || SOUNDS[0];
}

export function buildSound(ctx, id) {
  return findSound(id).build(ctx);
}

// Plays `buffer` at `time`, panned fully to one side.
function playPanned(ctx, buffer, time, side) {
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  let panner;
  try {
    panner = ctx.createStereoPanner();
    panner.pan.value = side === 'left' ? -1 : 1;
  } catch (e) {
    panner = ctx.createPanner();
    panner.panningModel = 'equalpower';
    panner.setPosition(side === 'left' ? -1 : 1, 0, 0);
  }
  source.connect(panner);
  panner.connect(ctx.destination);
  source.start(time);
  return source;
}

const otherSide = side => (side === 'left' ? 'right' : 'left');

const TICK_MS = 50;
// How far ahead beats are handed to the audio clock. Hidden tabs only get
// timers about once a second, so look further ahead there.
const LOOKAHEAD_MS = 150;
const HIDDEN_LOOKAHEAD_MS = 1200;
// A beat this late is dropped and the rhythm picks up from now
const LATE_MS = 100;
const MIN_PERIOD_MS = 40;
const PREVIEW_GAP_MS = 500;

// Alternating left/right playback, shared by the patient view and the
// therapist's optional local playback. The beats land on the ball's turns:
// BallStage reports each upcoming turn (syncTurn) and the time between turns
// (setTempo), and beats are scheduled ahead on the AudioContext clock. When no
// turn is coming (ball stopped, or the tab hidden so the ball isn't
// animating) the beats carry on at the same tempo.
export class BilateralPlayer {
  constructor() {
    this.ctx = null;
    this.buffer = null;
    this.onset = 0;
    this.soundId = DEFAULT_SOUND;
    this.periodMs = 500;
    this.timer = null;
    // Next beat to schedule: { side, at, fromBall }, `at` in performance.now() ms
    this.next = null;
    // Scheduled beats that haven't finished: { side, at, when, source }
    this.queue = [];
  }

  get unlocked() {
    return !!this.ctx;
  }

  // Browsers only allow audio after a user gesture, so call this from a
  // click/tap handler before the first start().
  unlock() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      this.loadSound();
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  setSound(id) {
    if (id === this.soundId) return;
    this.soundId = id;
    if (this.ctx) {
      this.loadSound();
    }
  }

  loadSound() {
    const sound = findSound(this.soundId);
    this.buffer = sound.build(this.ctx);
    this.onset = sound.onset || 0;
  }

  // Time between two turns of the ball, in ms
  setTempo(periodMs) {
    const ms = Math.max(MIN_PERIOD_MS, periodMs);
    if (!isFinite(ms)) return;
    // A free-running beat follows the new tempo straight away
    if (this.next && !this.next.fromBall) {
      this.next.at += ms - this.periodMs;
    }
    this.periodMs = ms;
  }

  // The ball's next turn: { side, at }
  syncTurn(turn) {
    const last = this.queue[this.queue.length - 1];
    const gap = last ? Math.abs(last.at - turn.at) : Infinity;
    if (last && last.side === turn.side && gap < this.periodMs / 2) {
      // Already scheduled: move it if the ball's timing changed and it
      // hasn't started playing yet
      if (gap > 4 && last.when > this.ctx.currentTime) {
        this.cancelPending();
        this.schedule(turn);
      }
      this.next = { side: otherSide(turn.side), at: turn.at + this.periodMs, fromBall: false };
      return;
    }
    if (last && turn.at < last.at + this.periodMs / 2) {
      // Beats queued past this turn (e.g. while the tab was hidden) are out of step
      this.cancelPending();
    }
    this.next = { side: turn.side, at: turn.at, fromBall: true };
  }

  get running() {
    return !!this.timer;
  }

  start() {
    if (!this.ctx || this.timer) return;
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    if (!this.next) {
      this.next = { side: 'left', at: performance.now(), fromBall: false };
    }
    this.tick();
  }

  tick() {
    const now = performance.now();
    const lookahead = document.hidden ? HIDDEN_LOOKAHEAD_MS : LOOKAHEAD_MS;
    const horizon = now + lookahead + (this.outputDelay() + this.onset) * 1000;
    let next = this.next;
    if (next.at < now - LATE_MS) {
      // Missed (timers throttled, or a stale turn from a ball that has
      // stopped): pick up the rhythm from now
      next = { side: next.side, at: now, fromBall: false };
    }
    while (next.at <= horizon) {
      this.schedule(next);
      next = { side: otherSide(next.side), at: next.at + this.periodMs, fromBall: false };
    }
    this.next = next;
    this.timer = setTimeout(() => this.tick(), TICK_MS);
  }

  // Seconds between scheduling a sample and hearing it
  outputDelay() {
    return Math.min(0.5, (this.ctx.baseLatency || 0) + (this.ctx.outputLatency || 0));
  }

  schedule(beat) {
    const ctx = this.ctx;
    // Start early by the output delay and the sound's onset so the peak is
    // heard as the ball turns
    const lead = this.outputDelay() + this.onset;
    const when = Math.max(ctx.currentTime, ctx.currentTime + (beat.at - performance.now()) / 1000 - lead);
    const source = playPanned(ctx, this.buffer, when, beat.side);
    const entry = { side: beat.side, at: beat.at, when, source };
    this.queue.push(entry);
    source.onended = () => {
      const i = this.queue.indexOf(entry);
      if (i !== -1) this.queue.splice(i, 1);
    };
  }

  // Drops scheduled beats that haven't started playing yet
  cancelPending() {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.queue = this.queue.filter(entry => {
      if (entry.when <= now) return true;
      entry.source.stop();
      return false;
    });
  }

  stop() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.cancelPending();
  }

  // A few alternating beats, e.g. to try a sound before using it
  preview(soundId, beats = 4) {
    this.unlock();
    const buffer = buildSound(this.ctx, soundId);
    const start = this.ctx.currentTime + 0.05;
    for (let i = 0; i < beats; i++) {
      playPanned(this.ctx, buffer, start + i * PREVIEW_GAP_MS / 1000, i % 2 ? 'right' : 'left');
    }
  }

  close() {
    this.stop();
    if (this.ctx) {
      this.ctx.close();
      this.ctx = null;
    }
    this.queue = [];
  }
}
